import { Component, Inject, OnInit, OnDestroy, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface MessageDialogData {
  title?: string;
  message: string;
  type?: 'success' | 'error' | 'info';
  primaryButtonText?: string;
  secondaryButtonText?: string;
}

@Component({
  selector: 'app-common-message-dialog',
  templateUrl: './common-message-dialog.component.html',
  styleUrls: ['./common-message-dialog.component.scss'],
})
export class CommonMessageDialogComponent implements OnInit, OnDestroy {
  private autoCloseTimeout: any;
  public isJustOkButton: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CommonMessageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MessageDialogData,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    // Determine if this is a simple alert/toast or a confirmation
    const isConfirmation = !!this.data.secondaryButtonText;
    const isErrorOrInfo = this.data.type === 'error' || this.data.type === 'info';

    if (isConfirmation || isErrorOrInfo) {
      this.isJustOkButton = false; // Show primary button (OK)
    } else {
      // It's a success toast
      this.isJustOkButton = true; // Hide footer button, rely on auto-close
      
      // Auto-close after 2.5 seconds
      this.ngZone.runOutsideAngular(() => {
        this.autoCloseTimeout = setTimeout(() => {
          this.ngZone.run(() => {
            this.confirm();
          });
        }, 2500);
      });
    }

    // Explicitly hide secondary button for success/error if it's "No" and we didn't mean it to be a choice
    // This addresses the user's report about unnecessary "No" buttons.
    if ((this.data.type === 'success' || this.data.type === 'error') && this.data.secondaryButtonText === 'No') {
      this.data.secondaryButtonText = undefined;
    }
  }

  ngOnDestroy() {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }
  }

  get title(): string {
    if (this.data.title) return this.data.title;
    switch (this.data.type) {
      case 'error':
        return 'Error';
      case 'info':
        return 'Information';
      default:
        return 'Success';
    }
  }

  get icon(): string {
    switch (this.data.type) {
      case 'error':
        return 'error';
      case 'info':
        return 'info';
      default:
        return 'check_circle';
    }
  }

  get iconColor(): string {
    switch (this.data.type) {
      case 'error':
        return '#f44336'; // Red
      case 'info':
        return '#2196f3'; // Blue
      default:
        return '#4caf50'; // Green
    }
  }

  close() {
    this.dialogRef.close(false);
  }

  confirm() {
    this.dialogRef.close(true);
  }
}
